import { TpCanonicalTypeInfo, TpDataContext } from '../interfaces';
import { TpUserEnSelectionAction } from './userSelectionActions';

// Enumeration of all possible types of selection filters (selection filters are applied to entities in datacontexts)
export enum TpEnSelectionFilterTypes {
  CATEGORICAL = 'Categorical',
  DUALCATEGORICAL = 'DualCategorical',
  INTERSECTIONS = 'Intersections',
  MULTILEVELCAT = 'MultilevelCategorical',
  NUMERICALRANGE = 'NumericalRange',
  LASSO2D = 'Lasso2D',
  MANUAL = 'Manual',
  IMPORT = 'Import',
}

// An entity filter step that filters according to a single categorical property
export interface TpEnSelectionFilterStepCategorical {
  type: TpEnSelectionFilterTypes.CATEGORICAL;
  key: string;
  dataTableId: string;
  colId: string;
  selectedCategoryIdList: any[];
  excludedCategoryIdList: any[];
  includedCount?: number;
}

// An entity filter step that filters according to a 2 categorical properties (e.g. for a contingency tablr)
export interface TpEnSelectionFilterStepDualCategorical {
  type: TpEnSelectionFilterTypes.DUALCATEGORICAL;
  key: string;
  dataTableId: string;
  colId1: string;
  colId2: string;
  selectedCellsList: { val1: any; val2: any }[];
  includedCount?: number;
}

// An entity filter step that filters according to a in intersection of sets, defined by a multi-valued categorical property
export interface TpEnSelectionFilterStepIntersections {
  type: TpEnSelectionFilterTypes.INTERSECTIONS;
  key: string;
  dataTableId: string;
  colId: string;
  intersections: any[][];
  includedCount?: number;
}

// An entity filter step that filters according to a multilevel categorical view (e.g. sunburst)
export interface TpEnSelectionFilterStepMultiLevelCat {
  type: TpEnSelectionFilterTypes.MULTILEVELCAT;
  key: string;
  dataTableId: string;
  colIds: string[];
  selectedElements: {
    key: string;
    label: string;
    selectedItemIds: string[];
  }[];
  includedCount?: number;
}

// An entity filter step that filters according to a range in a numerical property
export interface TpEnSelectionFilterStepNumericalRange {
  type: TpEnSelectionFilterTypes.NUMERICALRANGE;
  key: string;
  dataTableId: string;
  colId: string;
  rangeMin: number;
  rangeMax: number;
  includedCount?: number;
}

// An entity filter step that filters according to a lasso selection on a 2D plot
export interface TpEnSelectionFilterStepLasso2D {
  type: TpEnSelectionFilterTypes.LASSO2D;
  key: string;
  dataTableId: string;
  colIdX: string;
  colIdY: string;
  lassoPoints: { x: number; y: number }[];
  selectedItemIds: any[]; // set of included entity item IDs
  includedCount?: number;
}

// An entity filter step that filters according to a manual selection
export interface TpEnSelectionFilterStepManual {
  type: TpEnSelectionFilterTypes.MANUAL;
  description: string;
  key: string;
  selectedItemIds: any[]; // set if included entity item IDs
  includedCount?: number;
  stepIdentifier?: string;
}

// An entity filter step that is used for importing a manual selection
export interface TpEnSelectionFilterStepImport {
  type: TpEnSelectionFilterTypes.IMPORT;
  description: string;
  key: string;
  assetId: string;
  assetName?: string;
  selectedItemIds: string[]; // matched entity item IDs from the imported asset
  includedCount?: number;
}

// The combination of all possible filter steps
export type TpEnSelectionFilterStep =
  | TpEnSelectionFilterStepCategorical
  | TpEnSelectionFilterStepDualCategorical
  | TpEnSelectionFilterStepIntersections
  | TpEnSelectionFilterStepMultiLevelCat
  | TpEnSelectionFilterStepNumericalRange
  | TpEnSelectionFilterStepLasso2D
  | TpEnSelectionFilterStepManual
  | TpEnSelectionFilterStepImport;

// Abstract interface that declares the functions that must be implemented for a type of selection filter
export interface TpEnSelectionFilterDefinition {
  handleUserAction: (
    // called when a user action must be dispatched
    state: any,
    dataContext: TpDataContext,
    canonicalTypeInfo: TpCanonicalTypeInfo,
    userSelectionAction: TpUserEnSelectionAction,
  ) => void;
  performStep: (
    // executes a filter step by returning all entity item IDs that are passed by the filter
    dataContext: TpDataContext,
    canonicalTypeInfo: TpCanonicalTypeInfo,
    step: TpEnSelectionFilterStep,
  ) => any[];
  renderContent: (
    // returns a component that describes a filter step
    dataContext: TpDataContext,
    canonicalTypeInfo: TpCanonicalTypeInfo,
    step: TpEnSelectionFilterStep,
    forceString?: boolean,
  ) => any;
  getJsonSchema?: () => any; // returns the JSON schema for serialization of this filter type
  fromJson?: (dataContext: TpDataContext, dataTableId: string, jsonData: any) => TpEnSelectionFilterStep;
  toJson?: (dataContext: TpDataContext, dataTableId: string, step: TpEnSelectionFilterStep) => any; // returns the JSON representation of this filter step
}

// Holds the active entity filter for a canonical type
export interface TpActiveEnSelectionFilter {
  steps: TpEnSelectionFilterStep[];
  manualSelection: boolean;
}
